import { Schema } from "effect";
import { MoneySchema, type Money } from "./Money";

/**
 * Schema for OrderItem with validation rules including financial validation
 */
export const OrderItemSchema = Schema.Struct({
  unitPrice: MoneySchema,
  quantity: Schema.Number.pipe(
    Schema.int(),
    Schema.greaterThan(0, {
      message: () => "Quantity must be a positive integer",
    })
  ),
  linePrice: MoneySchema,
  name: Schema.String.pipe(Schema.nonEmptyString()),
  variant: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
  productNumber: Schema.optional(Schema.String.pipe(Schema.nonEmptyString())),
}).pipe(
  Schema.filter(
    (item) => {
      // Validate that linePrice = unitPrice * quantity (with small tolerance for floating point precision)
      const expectedLinePrice = item.unitPrice.multiply(item.quantity);
      const tolerance = 0.01; // 1 cent tolerance for floating point precision
      return (
        Math.abs(item.linePrice.subtract(expectedLinePrice).amount) <= tolerance
      );
    },
    {
      message: () => "Line price must equal unit price multiplied by quantity",
    }
  )
);

/**
 * OrderItem value object representing an item in an order with financial validation.
 * This ensures that linePrice = unitPrice * quantity and captures item data as a snapshot.
 */
export type OrderItem = Schema.Schema.Type<typeof OrderItemSchema>;

/**
 * Factory function to create and validate OrderItem
 */
export const createOrderItem = (
  data: unknown
): Schema.Schema.Encoded<typeof OrderItemSchema> =>
  Schema.decodeUnknownSync(OrderItemSchema)(data);

/**
 * Validation function that returns an Effect
 */
export const validateOrderItem = (data: unknown) =>
  Schema.decodeUnknown(OrderItemSchema)(data);

/**
 * Helper function to calculate line price from unit price and quantity
 */
export const calculateLinePrice = (
  unitPrice: Money,
  quantity: number
): Money => {
  return unitPrice.multiply(quantity).roundTwoDecimals(); // Round to 2 decimal places
};

/**
 * Factory function to create OrderItem with automatic line price calculation
 */
export const createOrderItemWithCalculation = (data: {
  unitPrice: Money;
  quantity: number;
  name: string;
  variant?: string;
  productNumber?: string;
}): Schema.Schema.Encoded<typeof OrderItemSchema> => {
  const linePrice = calculateLinePrice(data.unitPrice, data.quantity);
  return createOrderItem({
    ...data,
    linePrice,
  });
};
